(() => {
  'use strict';

  const COOKIE_NAME = 'mm_store_preference';
  const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
  const REQUEST_TIMEOUT_MS = 4000;
  const VALID_STORES = new Set(['international', 'jp']);

  const getCookie = (name) => {
    const prefix = `${encodeURIComponent(name)}=`;
    const cookie = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(prefix));

    if (!cookie) return null;

    try {
      return decodeURIComponent(cookie.slice(prefix.length));
    } catch {
      return null;
    }
  };

  const savePreference = (store) => {
    if (!VALID_STORES.has(store)) return;

    const expires = new Date(Date.now() + COOKIE_MAX_AGE * 1000).toUTCString();
    document.cookie = [
      `${COOKIE_NAME}=${encodeURIComponent(store)}`,
      `Max-Age=${COOKIE_MAX_AGE}`,
      `Expires=${expires}`,
      'Domain=.mutemotion.com',
      'Path=/',
      'SameSite=Lax',
      'Secure',
    ].join('; ');
  };

  const isKnownStoreHostname = (hostname, internationalOrigin, japanOrigin) => {
    return hostname === internationalOrigin.hostname || hostname === japanOrigin.hostname;
  };


  const getDestinationUrl = (targetOrigin, counterpartUrl) => {
    // Explicit equivalents only: same handles and bundle structures are not guaranteed.
    if (counterpartUrl) {
      const destination = new URL(counterpartUrl, targetOrigin);
      if (destination.origin === targetOrigin.origin) return destination.toString();
    }
    return targetOrigin.toString();
  };

  const getFocusableElements = (dialog) => {
    return Array.from(
      dialog.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hasAttribute('hidden'));
  };

  const initStoreRouter = async () => {
    if (window.Shopify?.designMode) return;

    const router = document.querySelector('[data-mm-store-router]');
    if (!(router instanceof HTMLDialogElement)) return;

    const currentStore = router.dataset.currentStore;
    if (!VALID_STORES.has(currentStore)) return;

    let internationalOrigin;
    let japanOrigin;

    try {
      internationalOrigin = new URL(router.dataset.internationalOrigin);
      japanOrigin = new URL(router.dataset.japanOrigin);
    } catch {
      return;
    }

    const debugMode = new URLSearchParams(window.location.search).get('mm_store_router_debug') === '1';
    const currentHostname = window.location.hostname.toLowerCase();

    if (!isKnownStoreHostname(currentHostname, internationalOrigin, japanOrigin) && !debugMode) return;

    const savedPreference = getCookie(COOKIE_NAME);
    if (VALID_STORES.has(savedPreference)) return;

    if (!window.Shopify?.routes?.root) return;

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    let detectedCountry;

    try {
      const endpoint =
        window.Shopify.routes.root + 'browsing_context_suggestions.json?country[enabled]=true';
      const response = await fetch(endpoint, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });

      if (!response.ok) return;

      const data = await response.json();
      detectedCountry = data?.detected_values?.country?.handle;
    } catch {
      return;
    } finally {
      window.clearTimeout(timeoutId);
    }

    if (typeof detectedCountry !== 'string') return;

    const country = detectedCountry.toUpperCase();
    const shouldRecommendJapan = currentStore === 'international' && country === 'JP';
    const shouldRecommendInternational = currentStore === 'jp' && country !== 'JP';

    if (!shouldRecommendJapan && !shouldRecommendInternational) return;

    const switchButton = router.querySelector('[data-mm-store-switch]');
    const stayButton = router.querySelector('[data-mm-store-stay]');

    if (!(switchButton instanceof HTMLButtonElement) || !(stayButton instanceof HTMLButtonElement)) return;

    const targetStore = currentStore === 'jp' ? 'international' : 'jp';
    const targetOrigin = targetStore === 'jp' ? japanOrigin : internationalOrigin;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const restorePageState = () => {
      document.body.classList.remove('mm-store-router-open');
      previouslyFocused?.focus({ preventScroll: true });
    };

    const trapFocus = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        router.close('escape');
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements(router);
      if (focusableElements.length === 0) {
        event.preventDefault();
        router.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    router.addEventListener('keydown', trapFocus);
    router.addEventListener('cancel', (event) => {
      event.preventDefault();
      router.close('escape');
    });
    router.addEventListener('close', restorePageState, { once: true });

    stayButton.addEventListener(
      'click',
      () => {
        savePreference(currentStore);
        router.close('stay');
      },
      { once: true }
    );

    switchButton.addEventListener(
      'click',
      () => {
        savePreference(targetStore);
        const destination = getDestinationUrl(targetOrigin, router.dataset.counterpartUrl);
        router.close('switch');
        window.location.assign(destination);
      },
      { once: true }
    );

    document.body.classList.add('mm-store-router-open');
    router.showModal();
    switchButton.focus();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStoreRouter, { once: true });
  } else {
    initStoreRouter();
  }
})();
